import { CornerDownRight } from "lucide-react";
import { getEffectiveRecurrence } from "@/lib/recurrence";
import { humaniseDateFriendly } from "@/lib/utils";
import { generateOccurrenceList } from "@/components/tpr/RecurrenceExceptions/generateOccurrenceList";
import type { Booking } from "@/types/Booking";

/**
 * The full occurrence list for a recurring booking (up to 1 year out, R1), rendered indented beneath
 * its row when the list is in "Active (all occurrences)" mode. Each occurrence is prefixed with a small
 * L-shaped connector (CornerDownRight) to show it hangs off the genesis booking, and sits on the same
 * grey background as the list. Holidays/exclusions and past dates are marked so a member can confirm
 * nothing is missing. Reuses generateOccurrenceList (past/excluded/available states, cancelledFrom-aware)
 * so it stays in step with the slot-holiday UI.
 */
const OccurrenceTree = ({ booking }: { booking: Booking }) => {
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) return null;
  const baseDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const occurrences = generateOccurrenceList(baseDate, recurrence);
  const time = `${booking.startTime.slice(0, 2)}:${booking.startTime.slice(2)}`;

  return (
    <div className="pl-8 py-1" data-testid={`occurrence-tree-${booking.id}`}>
      <div className="flex flex-col gap-1">
        {occurrences.map((occ) => (
          <div
            key={occ.iso}
            data-testid={`occurrence-${occ.iso}`}
            className={`flex items-center gap-2 text-xs ${occ.state === "past" ? "text-gray-400" : "text-gray-700"}`}
          >
            <CornerDownRight className="size-3.5 shrink-0 text-gray-400" aria-hidden />
            <span>{humaniseDateFriendly(occ.date)}</span>
            <span className="text-gray-400">at {time}</span>
            {occ.state === "excluded" && (
              <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                Holiday
              </span>
            )}
            {occ.state === "past" && <span className="text-gray-400">(past)</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OccurrenceTree;
